import classnames from 'classnames/bind'
import { useRef } from 'react'
import { GlobalThemeColors } from '~/@types/colors'

import { useMeasureObserver } from '@unlikelystudio/react-hooks'
import Slider, { useSliderState } from '@unlikelystudio/react-slider'

import SliderControls from '~/components/Abstracts/SliderControls'
import SliderArrows from '~/components/SliderArrows'
import { CardProductProps } from '~/components/UI/CardProduct'
import UpsaleItem from '~/components/UI/UpsaleItem'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface UpsalesSliderProps {
  className?: string
  items?: CardProductProps[]
  layout?: 'product-page' | 'cart'
  loading?: boolean
}

function UpsalesSlider({
  className,
  items,
  loading,
  layout = 'cart',
}: UpsalesSliderProps) {
  const customSliderRef = useRef()
  const [{ slideIndex, nextSlide, prevSlide, setSlideIndex }, setSliderState] =
    useSliderState()

  const sliderBounds = useMeasureObserver(
    customSliderRef,
    'getBoundingClientRect',
  )

  return (
    <div
      className={cx(className, css.UpsalesSlider, css?.[`layout-${layout}`])}>
      {((items && items?.length > 0) || loading) && (
        <>
          <div className={css.sliderContainer} ref={customSliderRef}>
            <Slider
              className={cx(css.slider)}
              customSliderRef={customSliderRef}
              containerClassName={cx(css.slidesContainer, {
                [css.isLoading]: loading,
              })}
              autoIsSlider={true}
              snap
              setSliderState={setSliderState}>
              {!loading
                ? items.map((upsale, index) => {
                    return (
                      <div
                        key={`loading_item_${index}`}
                        className={css.sliderItemWrapper}
                        style={
                          layout === 'cart'
                            ? {}
                            : {
                                width: sliderBounds?.width
                                  ? `${sliderBounds?.width}px`
                                  : '100%',
                              }
                        }>
                        <UpsaleItem
                          className={css.sliderItem}
                          layout={layout}
                          {...upsale}
                        />
                      </div>
                    )
                  })
                : Array(1)
                    .fill(null)
                    .map((_, index) => (
                      <div
                        key={`loading_upsell_item_${index}`}
                        className={css.sliderItemWrapper}
                        style={
                          layout === 'cart'
                            ? {}
                            : {
                                width: `100%`,
                              }
                        }>
                        <UpsaleItem
                          className={css.sliderItem}
                          isFetching
                          layout={layout}
                        />
                      </div>
                    ))}
            </Slider>
            {items?.length > 1 && (
              <div className={cx(css.arrowsContainer)}>
                <SliderArrows
                  layout={layout === 'cart' ? 'inside' : null}
                  index={slideIndex}
                  length={items?.length ?? 0}
                  buttonsLayout="xs"
                  theme={
                    layout === 'cart'
                      ? GlobalThemeColors.Black
                      : GlobalThemeColors.White
                  }
                  nextSlide={nextSlide}
                  prevSlide={prevSlide}
                />
              </div>
            )}
          </div>
          {layout !== 'cart' && (
            <SliderControls
              className={css.sliderControls}
              length={loading ? 2 : items?.length}
              slideIndex={loading ? 1 : slideIndex}
              setSlideIndex={setSlideIndex}
            />
          )}
        </>
      )}
    </div>
  )
}

UpsalesSlider.defaultProps = {}

export default UpsalesSlider
